import {
  BuildingIcon,
  DownloadIcon,
  FilterXIcon,
  RefreshCwIcon,
  SearchIcon,
  UploadIcon,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { contactName, CustomerSort, primaryContact, type Customer } from '@/api/customers'
import { exportCustomersForOdoo, holdsNoRows, saveBlob } from '@/api/customerTransfer'
import { useApiErrorMessage } from '@/api/errors'
import { Role } from '@/auth/roles'
import { useAuth } from '@/auth/useAuth'
import { ClearFiltersButton } from '@/components/ClearFiltersButton'
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardFooter } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ActivatableRow } from '@/components/layout/ActivatableRow'
import { PageHeader } from '@/components/layout/PageHeader'
import { Pagination } from '@/components/layout/Pagination'
import { SortableHead } from '@/components/layout/SortableHead'
import {
  EDGE_PADDED_TABLE,
  FULL_HEIGHT_CARD,
  PHONE_SUMMARY,
  PHONE_WRAPPING_CELL,
  SCROLLING_TABLE,
  STICKY_TABLE_HEAD,
  WIDE_ONLY,
} from '@/components/layout/tableStyles'
import { formatAddress } from '@/components/customers/address'
import { CustomerDetailDialog } from '@/components/customers/CustomerDetailDialog'
import { CustomerImportDialog } from '@/components/customers/CustomerImportDialog'
import { CustomerRowActions } from '@/components/customers/CustomerRowActions'
import { useCustomerList } from '@/hooks/useCustomerList'
import { cn } from '@/lib/utils'

const COLUMN_COUNT = 4

/**
 * The customer master data. Read-only for now — capturing and editing a customer happens on the
 * lead screen, which is the next thing to build; the API behind this page already supports both.
 */
export function CustomersPage() {
  const { t, i18n } = useTranslation()
  const language = i18n.resolvedLanguage ?? 'de'
  const { hasRole } = useAuth()
  const errorMessage = useApiErrorMessage()

  // Deleting takes a company out of everybody's list, so it is an administrator's — and so is
  // looking at what has been deleted: a salesperson can neither delete nor restore one, so a row
  // marked "Gelöscht" is only a record they cannot act on sitting in the middle of the ones they
  // can. They get no checkbox for it and no way to ask for it.
  const canDelete = hasRole(Role.ADMIN)
  /**
   * So is moving the whole address book. Everybody in sales reads and writes customers one at a
   * time; taking the lot out of the building, or rewriting hundreds of them from a file, is a
   * different act — and the backend refuses it for anybody else regardless of this flag.
   */
  const canTransfer = hasRole(Role.ADMIN)

  const list = useCustomerList(canDelete)

  const [showing, setShowing] = useState<Customer | null>(null)
  const [importing, setImporting] = useState(false)
  const [exporting, setExporting] = useState(false)

  async function download() {
    setExporting(true)
    try {
      const { blob, filename } = await exportCustomersForOdoo()
      // A file of headers alone would be imported into Odoo for nothing; saying so is the answer.
      if (await holdsNoRows(blob)) {
        toast.info(t('customerTransfer.nothingNew'))
        return
      }
      // The backend's name carries the date. Ours is only there if the header went missing.
      saveBlob(blob, filename ?? t('customerTransfer.filename'))
    } catch (error) {
      toast.error(errorMessage(error, t('customerTransfer.exportFailed')))
    } finally {
      setExporting(false)
    }
  }

  const customers = list.page?.customers ?? []
  const total = list.page?.total ?? 0

  return (
    <TooltipProvider>
      <PageHeader title={t('customers.title')}>
        {canTransfer && (
          <>
            <Button variant="outline" disabled={exporting} onClick={() => void download()}>
              <DownloadIcon />
              {t('customerTransfer.export')}
            </Button>
            <Button variant="outline" onClick={() => setImporting(true)}>
              <UploadIcon />
              {t('customerTransfer.import')}
            </Button>
          </>
        )}
      </PageHeader>

      <Card className={FULL_HEIGHT_CARD}>
        <div className="flex flex-wrap items-center gap-3 border-b p-4 shrink-0">
          <div className="relative min-w-56 flex-1">
            <SearchIcon
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
              aria-hidden
            />
            <Input
              type="search"
              className="pl-8"
              aria-label={t('customers.searchLabel')}
              placeholder={t('customers.searchPlaceholder')}
              value={list.search}
              onChange={(event) => list.setSearch(event.target.value)}
            />
          </div>

          {canDelete && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="include-deleted-customers"
                checked={list.includeDeleted}
                onCheckedChange={(checked) => list.setIncludeDeleted(checked === true)}
              />
              <Label htmlFor="include-deleted-customers" className="text-sm font-normal">
                {t('customers.includeDeleted')}
              </Label>
            </div>
          )}

          <ClearFiltersButton
            active={list.filtered}
            label={t('common.clearFilters')}
            onClick={list.clearFilters}
          />

          <Button
            variant="outline"
            size="icon"
            aria-label={t('customers.reload')}
            onClick={list.reload}
          >
            <RefreshCwIcon />
          </Button>
        </div>

        {list.error ? (
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <Alert variant="destructive">
              <AlertTitle>{t('customers.loadFailed')}</AlertTitle>
              <AlertDescription>
                {errorMessage(list.error, t('common.unexpectedError'))}
              </AlertDescription>
              <AlertAction>
                <Button variant="outline" size="sm" onClick={list.reload}>
                  {t('customers.retry')}
                </Button>
              </AlertAction>
            </Alert>
          </div>
        ) : (
          <Table
            className={EDGE_PADDED_TABLE}
            containerClassName={SCROLLING_TABLE}
            // The rows on screen are the previous page while the next one loads. This is what
            // says so to a screen reader; the dimming below is what says it to everybody else.
            aria-busy={list.loading}
          >
            <TableHeader className={STICKY_TABLE_HEAD}>
              <TableRow>
                <SortableHead
                  column={CustomerSort.COMPANY_NAME}
                  sort={list.sort}
                  direction={list.direction}
                  onToggle={list.toggleSort}
                >
                  {t('customers.columns.company')}
                </SortableHead>
                {/* Not sortable: the person shown is whichever contact is the primary one, which
                    is a choice made per row rather than a column the database has. */}
                <TableHead className={WIDE_ONLY}>{t('customers.columns.contact')}</TableHead>
                <SortableHead
                  column={CustomerSort.CITY}
                  sort={list.sort}
                  direction={list.direction}
                  onToggle={list.toggleSort}
                  className={WIDE_ONLY}
                >
                  {t('customers.columns.location')}
                </SortableHead>
                <TableHead className="w-0 text-right">
                  <span className="sr-only">{t('customers.columns.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody
              // Held back while the next page is on its way, rather than blanked: the rows are
              // still true, they are just no longer the answer to what was asked.
              className={list.loading && customers.length > 0 ? 'opacity-60 transition-opacity' : undefined}
            >
              {/* Only while there is nothing to show: repainting a populated table as skeletons on
                  every keystroke is worse than leaving the old rows up. */}
              {list.loading && customers.length === 0 && <LoadingRows />}

              {!list.loading && customers.length === 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={COLUMN_COUNT} className="h-40">
                    <div className="text-muted-foreground flex flex-col items-center justify-center gap-2 text-center">
                      <BuildingIcon className="size-6" aria-hidden />
                      <p className="text-sm">
                        {list.search
                          ? t('customers.emptySearch', { search: list.search })
                          : t('customers.empty')}
                      </p>
                      {list.filtered && (
                        <Button variant="outline" size="sm" onClick={list.clearFilters}>
                          <FilterXIcon />
                          {t('common.clearFilters')}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {customers.map((customer) => {
                const contact = primaryContact(customer)
                const address = formatAddress(customer, language)
                return (
                  <ActivatableRow
                    key={customer.id}
                    data-testid={`customer-${customer.id}`}
                    onActivate={() => setShowing(customer)}
                  >
                    <TableCell className={PHONE_WRAPPING_CELL}>
                      <div className="flex flex-col">
                        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium">
                          {customer.companyName}
                          {customer.deleted && (
                            <Badge variant="outline" className="font-normal">
                              {t('customers.deleted')}
                            </Badge>
                          )}
                        </span>
                        {customer.customerNumber && (
                          <span className="text-muted-foreground text-xs">
                            {customer.customerNumber}
                          </span>
                        )}
                        {(contact || address) && (
                          <span className={PHONE_SUMMARY}>
                            {[contact && contactName(contact), address]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={WIDE_ONLY}>
                      {contact ? (
                        <div className="flex flex-col">
                          <span>{contactName(contact)}</span>
                          <span className="text-muted-foreground text-xs">
                            {/* The count only when there is more than one — "1 Ansprechpartner"
                                next to their name says nothing. */}
                            {customer.contacts.length > 1
                              ? t('customers.moreContacts', { count: customer.contacts.length - 1 })
                              : (contact.position ?? '')}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground italic">
                          {t('customers.noContact')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className={cn('text-muted-foreground', WIDE_ONLY)}>
                      {address ?? t('common.none')}
                    </TableCell>
                    <TableCell
                      className="text-right"
                      // The row opens the details; the menu inside it must not do both.
                      onClick={(event) => event.stopPropagation()}
                    >
                      <CustomerRowActions
                        customer={customer}
                        canDelete={canDelete}
                        onShowDetails={() => setShowing(customer)}
                        onChanged={list.reload}
                      />
                    </TableCell>
                  </ActivatableRow>
                )
              })}
            </TableBody>
          </Table>
        )}

        {!list.error && total > 0 && (
          <CardFooter className="block shrink-0">
            <Pagination
              pageIndex={list.pageIndex}
              pageCount={list.pageCount}
              pageSize={list.pageSize}
              range={list.range}
              onPageChange={list.setPageIndex}
              onPageSizeChange={list.setPageSize}
            />
          </CardFooter>
        )}
      </Card>

      <CustomerImportDialog
        open={importing}
        onOpenChange={setImporting}
        onImported={list.reload}
      />

      <CustomerDetailDialog
        customer={showing}
        onOpenChange={(open) => !open && setShowing(null)}
      />
    </TooltipProvider>
  )
}

function LoadingRows() {
  return (
    <>
      {Array.from({ length: 5 }, (_, index) => (
        <TableRow key={index} className="hover:bg-transparent">
          <TableCell colSpan={COLUMN_COUNT} className="py-3">
            <Skeleton className="h-8 w-full" />
          </TableCell>
        </TableRow>
      ))}
    </>
  )
}
